import { DatePipe, NgClass } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { Dialog } from 'primeng/dialog';
import { SelectButton } from 'primeng/selectbutton';
import { TableModule, TableRowSelectEvent } from 'primeng/table';
import { Tag } from 'primeng/tag';
import { SERVICE_ENDPOINTS } from '../../../../../core/configs/service-endpoints';
import { KeyToIconPipe } from '../../../../../core/pipes/key-to-icon.pipe';
import { HelperService } from '../../../../../core/services/helper.service';
import { I18nService } from '../../../../../core/services/i18n.service';
import { UserSessionSettingsProvider } from '../../../../../core/services/user-session-settings-provider.service';
import { SpsCustomIconComponent } from '../../../../../custom/components/sps-custom-icon/sps-custom-icon.component';
import { SpsTransactionDetailsDialogComponent } from '../../../../../custom/components/sps-transaction-details-dialog/sps-transaction-details-dialog.component';
import { LayoutService } from '../../../../../layout/services/layout.service';
import { TransactionCreate } from '../../../../dtos/transaction/transaction-create';
import { PaymentMethodName } from '../../../../enums/payment/payment-method-name';
import { TransactionData } from '../../../../interfaces/transaction/transaction-data';
import { TransactionDetailsDialog } from '../../../../interfaces/transaction/transaction-details-dialog';
import { TransactionQueryConfig } from '../../../../interfaces/transaction/transaction-query-config';
import { TransactionDataToTransactionCreateMapperService } from '../../../../mapper/transaction/transaction-data-to-transaction-create-mapper.service';
import { TransactionToTransactionViewDataMapperService } from '../../../../mapper/transaction/transaction-to-transaction-view-data-mapper.service';
import { AmountCurrencyDisplayPipe } from '../../../../pipes/amount-currency-display.pipe';
import { LatestTransactionsPollingService } from '../../../../services/polling/latest-transactions-polling.service';
import { TransactionDataService } from '../../../../services/transaction-data.service';
import { TransactionHandlingService } from '../../../../services/transaction-handling.service';
import { DbNoDataInfoComponent } from '../db-no-data-info/db-no-data-info.component';

@Component({
  selector: 'db-latest-transactions',
  imports: [
    NgClass,
    Card,
    TableModule,
    KeyToIconPipe,
    DatePipe,
    AmountCurrencyDisplayPipe,
    Tag,
    SpsCustomIconComponent,
    DbNoDataInfoComponent,
    Button,
    Dialog,
    SpsTransactionDetailsDialogComponent,
    SelectButton,
    FormsModule
  ],
  templateUrl: './db-latest-transactions.component.html'
})
export class DbLatestTransactionsComponent implements OnInit, OnDestroy {

  /**
   * Data-Loading flag on initializing transaction-data
   */
  loading: boolean = true;

  /**
   * Transaction and History data-objects
   */
  model: TransactionData[] = [];
  selectedTransaction: TransactionData = null;

  /** Details Dialog Initialization-Object */
  transactionDetailsDialog: TransactionDetailsDialog = {visible: false};
  public transactionViewMode: 'TENANT' | 'ADMIN' = 'TENANT';
  transactionDetailsId: string;
  transactionDetailsHeader: string;
  readonly showTransactionError = signal<boolean>(false);
  readonly transactionErrorMessage = signal<String>('');


  /** Options and Models */
  stateOptions = [
    {value: 'TEST', label: 'Test'},
    {value: 'LIVE', label: 'Live'}
  ];

  // changed to LIVE for default on stakeholder request
  // currentState: string = 'TEST';
  currentState: string = 'LIVE';

  /**
   * Params for Data-Refresh-Polling
   */
  transactionQueryConfig: TransactionQueryConfig;
  pollingService = inject(LatestTransactionsPollingService);
  lastDataHash: string = '';

  /**
   * Service injections
   */
  i18n = inject(I18nService);
  layoutService = inject(LayoutService);
  private helper = inject(HelperService);
  private userSessionSettingsProvider = inject(UserSessionSettingsProvider);
  private transactionDataService = inject(TransactionDataService);
  private transactionHandlingService = inject(TransactionHandlingService);
  private transactionDataMapper = inject(TransactionToTransactionViewDataMapperService);
  private transactionCreateMapper = inject(TransactionDataToTransactionCreateMapperService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);

  /**
   * get transaction search params and transaction data
   */
  ngOnInit(): void {
    if (this.userSessionSettingsProvider.hasValue('LATEST_TRANSACTIONS_STATE')) {
      this.currentState = this.userSessionSettingsProvider.get('LATEST_TRANSACTIONS_STATE');
    }
    this.getLatestTransactions();
  }

  ngOnDestroy(): void {
    this.pollingService.unsubscribePolling();
  }

  getLatestTransactions(): any {
    const searchParamsConfig: string = this.currentState === 'TEST' ? 'latest-transactions-test' : 'latest-transactions-live';
    this.transactionDataService.getQueryParamConfig(searchParamsConfig).subscribe({
      next: params => {
        this.transactionQueryConfig = params;
        this.transactionDataService.getLatestTransactions(this.transactionQueryConfig).subscribe({
          next: result => {
            if (result.successful) {
              this.model = result.payload;
              this.lastDataHash = this.helper.dataArrayHash(this.model);
              /**
               * Subscribe the polling service for incoming transactions
               */
              if (!this.pollingService.isSubscribed) {
                console.log('Subscribing Polling-Service');
                this.pollingService.subscribePolling(this);
              }
              this.model.map(trans => {
                return this.transactionDataMapper.map(trans);
              });
            } else {
              this.messageService.add({
                sticky: true,
                severity: 'error',
                summary: result.message,
                detail: result.details + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
              });
            }
            this.loading = false;
          },
          error: err => {
            this.loading = false;
            this.messageService.add({
              sticky: true,
              severity: 'error',
              summary: this.i18n.i18nLng()['HTTPDATAERROR'],
              detail: err.message + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
            });
          }
        });
      },
      error: (error) => {
        this.loading = false;
        this.messageService.add({
          sticky: true,
          severity: 'error',
          summary: this.i18n.i18nLng()['HTTPDATAERROR'],
          detail: error.message + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
        });
      }
    });
  }

  onRowSelected(event: TableRowSelectEvent<TransactionData>) {
    if (this.selectedTransaction) {
      this.transactionDetailsId = this.selectedTransaction.id;
      this.transactionDetailsHeader = this.i18n?.i18nLng()['TRANSACTION'] + ': ' + this.selectedTransaction.id
      this.transactionDetailsDialog = {
        type: PaymentMethodName[this.selectedTransaction.paymentMethod],
        visible: true
      };
    }
  }

  /**
   * Close Transactions Details Dialog
   */
  closeDetailsDialog() {
    this.transactionErrorMessage.set('');
    this.showTransactionError.set(false);
    this.transactionDetailsDialog.visible = false;
    this.selectedTransaction = null;
  }

  /**
   * Refresh table when status changed
   */
  tableStatusChanged() {
    /**
     * works without delay and refresh
     this.loading = true;
     setTimeout(() => {
     this.userSessionSettingsProvider.set('LATEST_TRANSACTIONS_STATE', this.currentState);
     this.getLatestTransactions();
     }, 50);
     */

    this.userSessionSettingsProvider.set('LATEST_TRANSACTIONS_STATE', this.currentState);
    this.getLatestTransactions();

  }

  /**
   * History transaction was selected in the details dialog
   * change the dialog title to the new Id
   * @param transactionId
   * @protected
   */
  protected onTransactionIdChanged(transactionId: string) {
    this.transactionDetailsId = transactionId;
    this.transactionDetailsHeader = this.i18n?.i18nLng()['TRANSACTION'] + ': ' + this.transactionDetailsId
  }

  /**
   * Capture event was fired
   * confirm and process the capture
   * @param transactionData
   * @protected
   */
  protected onProcessCapture(transactionData: TransactionData) {
    const formattedAmount = new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: transactionData.currency,
    }).format(transactionData.amount / 100);

    this.confirmationService.confirm({
      position: 'center',
      message: (this.i18n.i18nLng()['CONFIRMCAPTURE']).replace("#1", formattedAmount).replace("#2", transactionData.id),
      header: this.i18n.i18nLng()['CONFIRMATION'],
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-question-circle',
      acceptButtonProps: {
        label: this.i18n.i18nLng()['CAPTUREBUTTON']
      },
      rejectButtonProps: {
        label: this.i18n.i18nLng()['CANCEL'],
        outlined: true,
        severity: 'secondary'
      },
      accept: () => {
        this.layoutService.progressDialogTitle = this.i18n.i18nLng()['PROCESSINGTRANSACTION'];
        this.layoutService.showProgressSpinner = true;
        this.layoutService.showProgressDialog = true;

        const transactionCreate: TransactionCreate = this.transactionCreateMapper.map(transactionData, 'CAPTURE');

        this.transactionHandlingService.createChildTransaction(SERVICE_ENDPOINTS.CREATE_TRANSACTION, 'capture', transactionData.id, transactionCreate).subscribe({
          next: result => {
            this.layoutService.showProgressDialog = false;
            if (result.successful) {
              this.closeDetailsDialog();
              this.messageService.add({
                life: 3500,
                severity: 'success',
                summary: this.i18n.i18nLng()['CAPTURE'],
                detail: this.i18n.i18nLng()['TRANSACTIONSUCCESSFUL_MSG']
              });
            } else {
              this.transactionErrorMessage.set(this.i18n.i18nLng()['PROCESSTRANSACTIONERROR'] + ': ' + result.message);
              this.showTransactionError.set(true);
            }
          },
          error: err => {
            this.layoutService.showProgressDialog = false;
            this.transactionErrorMessage.set(this.i18n.i18nLng()['PROCESSTRANSACTIONERROR'] + ': ' + err.message);
            this.showTransactionError.set(true);
          }
        })
      }
    });
  }

  /**
   * void event was fired
   * confirm and process the void
   * @param transactionData
   * @protected
   */
  protected onProcessVoid(transactionData: TransactionData) {
    const formattedAmount = new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: transactionData.currency,
    }).format(transactionData.amount / 100);

    this.confirmationService.confirm({
      position: 'center',
      message: (this.i18n.i18nLng()['CONFIRMVOID']).replace("#1", this.i18n.i18nLng()['VOID_' + transactionData.type]).replace("#2", formattedAmount).replace("#3", transactionData.id),
      header: this.i18n.i18nLng()['CONFIRMATION'],
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-question-circle',
      acceptButtonProps: {
        label: this.i18n.i18nLng()['CANCELLATIONBUTTON']
      },
      rejectButtonProps: {
        label: this.i18n.i18nLng()['CANCEL'],
        outlined: true,
        severity: 'secondary'
      },
      accept: () => {
        this.layoutService.progressDialogTitle = this.i18n.i18nLng()['PROCESSINGTRANSACTION'];
        this.layoutService.showProgressSpinner = true;
        this.layoutService.showProgressDialog = true;

        const transactionCreate: TransactionCreate = this.transactionCreateMapper.map(transactionData, 'VOID');

        this.transactionHandlingService.createChildTransaction(SERVICE_ENDPOINTS.CREATE_TRANSACTION, 'void', transactionData.id, transactionCreate).subscribe({
          next: result => {
            this.layoutService.showProgressDialog = false;
            if (result.successful) {
              this.closeDetailsDialog();
              this.messageService.add({
                life: 3500,
                severity: 'success',
                summary: this.i18n.i18nLng()['VOID'],
                detail: this.i18n.i18nLng()['TRANSACTIONSUCCESSFUL_MSG']
              });
            } else {
              this.transactionErrorMessage.set(this.i18n.i18nLng()['PROCESSTRANSACTIONERROR'] + ': ' + result.message);
              this.showTransactionError.set(true);
            }
          },
          error: err => {
            this.layoutService.showProgressDialog = false;
            this.transactionErrorMessage.set(this.i18n.i18nLng()['PROCESSTRANSACTIONERROR'] + ': ' + err.message);
            this.showTransactionError.set(true);
          }
        })
      }
    });
  }

  /**
   * show message and close dialog
   * @protected
   */
  protected onRefundSuccessful() {
    this.closeDetailsDialog();
    this.messageService.add({
      life: 3500,
      severity: 'success',
      summary: this.i18n.i18nLng()['REFUND'],
      detail: this.i18n.i18nLng()['TRANSACTIONSUCCESSFUL_MSG']
    });
  }

  protected onCreditSuccessful() {
    this.closeDetailsDialog();
    this.messageService.add({
      life: 3500,
      severity: 'success',
      summary: this.i18n.i18nLng()['CHARGEBACK'],
      detail: this.i18n.i18nLng()['TRANSACTIONSUCCESSFUL_MSG']
    });
  }
}
